/**
 * Keanggotaan Page
 * 
 * Displays a table of members with pagination.
 */

import * as React from "react";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { SetEmailDialog } from "./SetEmailDialog";
import { MemberFilters } from "./components/MemberFilters";
import { MemberTable } from "./components/MemberTable";
import { useMembersFilters } from "./hooks/useMembersFilters";
import { useMembersData, FIXED_PAGE_SIZE } from "./hooks/useMembersData";
import { userService } from "@/services/userService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Loader2, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getUserLocationScope } from "@/utils/locationScope";
import { getNumericUserStage, hasAssignmentPrivilege } from "@/utils/privileges";

// Fixed page size for this page - ensures consistent pagination
// This value is enforced in both frontend and backend
const PAGE_SIZE = FIXED_PAGE_SIZE;

export function KeanggotaanPage() {
  // Set page title
  React.useEffect(() => {
    document.title = "Daftar Anggota - ORARI";
  }, []);

  // Get user for admin assignments
  const { user } = useAuth();

  const userStage = getNumericUserStage(user);
  const isNasionalStage3 = userStage === 3;
  const isCityAdminStage1 = userStage === 1;
  const isProvinceAdminStage2 = userStage === 2;

  const [locationScope, setLocationScope] = React.useState<{ provinces: string[]; cities: string[]; level: "nasional" | "province" | "city" | null } | null>(null);

  React.useEffect(() => {
    if (user) {
      getUserLocationScope(user).then((scope) => {
        setLocationScope(scope);
      });
    }
  }, [user]);

  const allowedCities = React.useMemo(() => {
    if (isNasionalStage3) return undefined;
    if (!isCityAdminStage1) return undefined;
    const citiesSet = new Set<string>();
    user?.adminAssignments?.forEach((assignment) => {
      if (!assignment.isActive || assignment.level !== "city") return;
      assignment.cities?.forEach((c) => {
        if (c?.trim()) citiesSet.add(c.trim());
      });
    });
    if (citiesSet.size > 0) return Array.from(citiesSet);
    if (locationScope?.cities?.length) {
      return locationScope.cities.map((c) => c.trim()).filter(Boolean);
    }
    const profileKab = user?.kabupaten?.trim();
    if (profileKab) return [profileKab];
    return undefined;
  }, [isNasionalStage3, isCityAdminStage1, user?.adminAssignments, user?.kabupaten, locationScope?.cities]);

  const allowedProvinces = React.useMemo(() => {
    if (isNasionalStage3) return undefined;
    if (isCityAdminStage1) {
      const p = user?.provinsi?.trim();
      return p ? [p] : undefined;
    }
    if (isProvinceAdminStage2) {
      const provincesSet = new Set<string>();
      user?.adminAssignments?.forEach((assignment) => {
        if (!assignment.isActive || assignment.level !== "province") return;
        assignment.provinces?.forEach((p) => {
          if (p?.trim()) provincesSet.add(p.trim());
        });
      });
      if (provincesSet.size > 0) return Array.from(provincesSet);
      if (locationScope?.provinces?.length) {
        return locationScope.provinces.map((p) => p.trim()).filter(Boolean);
      }
      const profileProv = user?.provinsi?.trim();
      if (profileProv) return [profileProv];
      return undefined;
    }

    if (!user?.adminAssignments || user.adminAssignments.length === 0) {
      return undefined;
    }

    const provinceAssignments = user.adminAssignments.filter(
      (assignment) => assignment.isActive && assignment.level === "province"
    );

    if (provinceAssignments.length === 0) {
      return undefined;
    }

    const provincesSet = new Set<string>();
    provinceAssignments.forEach((assignment) => {
      if (assignment.provinces && Array.isArray(assignment.provinces)) {
        assignment.provinces.forEach((p) => {
          if (p && p.trim()) {
            provincesSet.add(p.trim());
          }
        });
      }
    });

    return provincesSet.size > 0 ? Array.from(provincesSet) : undefined;
  }, [user?.adminAssignments, user?.provinsi, isNasionalStage3, isCityAdminStage1, isProvinceAdminStage2, locationScope?.provinces]);

  /** Level 2: provinsi terkunci. Level 1: provinsi & kota terkunci (wilayah kota/kab). */
  const provinceFilterLocked = !isNasionalStage3 && (isProvinceAdminStage2 || isCityAdminStage1);
  const cityFilterLocked = !isNasionalStage3 && isCityAdminStage1;

  const lockedProvince = React.useMemo(() => {
    if (isNasionalStage3) return undefined;
    if (isCityAdminStage1) {
      return user?.provinsi?.trim() || undefined;
    }
    if (isProvinceAdminStage2 && allowedProvinces?.length === 1) {
      return allowedProvinces[0];
    }

    if (!user?.adminAssignments || user.adminAssignments.length === 0) {
      return undefined;
    }

    const provinceAssignment = user.adminAssignments.find(
      (assignment) => assignment.isActive && assignment.level === "province"
    );

    if (provinceAssignment && provinceAssignment.provinces && Array.isArray(provinceAssignment.provinces) && provinceAssignment.provinces.length === 1) {
      const firstProvince = provinceAssignment.provinces[0];
      return firstProvince ? firstProvince.trim() : undefined;
    }

    return undefined;
  }, [user?.adminAssignments, isNasionalStage3, isCityAdminStage1, isProvinceAdminStage2, allowedProvinces]);

  const lockedCity = React.useMemo(() => {
    if (isNasionalStage3) return undefined;
    if (!isCityAdminStage1 || !allowedCities?.length) return undefined;
    if (allowedCities.length === 1) return allowedCities[0];
    const profileKab = user?.kabupaten?.trim();
    if (profileKab && allowedCities.includes(profileKab)) return profileKab;
    return allowedCities[0];
  }, [isNasionalStage3, isCityAdminStage1, allowedCities, user?.kabupaten]);

  // Use a ref to store the setCurrentCursor function so we can call it from the filter change callback
  const setCurrentCursorRef = React.useRef<((cursor: string | null) => void) | null>(null);
  
  // Filters management - pass lockedProvince to prevent it from being cleared
  // Pass onFilterChange callback to reset cursor whenever filters change
  const filters = useMembersFilters(lockedProvince, () => {
    // Reset cursor whenever filters change
    if (setCurrentCursorRef.current) {
      setCurrentCursorRef.current(null);
    }
  }, lockedCity);
  
  // Members data management — lockedProvince dipakai saat URL tidak ada ?province= (mis. hanya cursor)
  const membersData = useMembersData({
    updateUrlWithCursor: filters.updateUrlWithCursor,
    lockedProvince,
    lockedCity,
  });
  
  // Store setCurrentCursor in ref so it can be accessed from the filter change callback
  React.useEffect(() => {
    setCurrentCursorRef.current = membersData.setCurrentCursor;
  }, [membersData.setCurrentCursor]);

  // Apply default location scope filter on initial load if no URL params
  // Also ensure lockedProvince is always set in URL (only if no province is already selected)
  React.useEffect(() => {
    // Only run on initial mount or when lockedProvince changes, not when user manually changes province
    const urlParams = new URLSearchParams(window.location.search);
    const hasProvinceInUrl = urlParams.has("province");
    const urlProvince = urlParams.get("province");
    const hasCityInUrl = urlParams.has("city");
    const urlCity = urlParams.get("city");
    const cursorInUrl = urlParams.has("cursor");

    if (isCityAdminStage1) {
      if (lockedProvince && lockedCity) {
        const needSync =
          !hasProvinceInUrl ||
          !hasCityInUrl ||
          urlProvince !== lockedProvince ||
          urlCity !== lockedCity;
        if (needSync && !cursorInUrl) {
          const currentSearch = urlParams.get("search") || "";
          filters.setSelectedProvince(lockedProvince);
          filters.setSelectedCity(lockedCity);
          filters.updateFiltersInUrl(currentSearch, lockedProvince, lockedCity);
          membersData.setCurrentCursor(null);
          membersData.fetchMembers(
            "next",
            null,
            currentSearch || undefined,
            lockedProvince,
            lockedCity,
            undefined,
            undefined,
            undefined,
            filters.tipeIar,
            filters.sortBy,
            filters.sortOrder
          );
        }
      }
      return;
    }

    // If lockedProvince exists and no province is already selected in URL, set state.
    // Jangan panggil updateFiltersInUrl bila ada cursor — itu menghapus pagination; useMembersData akan mengisi province lewat lockedProvince + updateUrlWithCursor.
    if (lockedProvince && !hasProvinceInUrl) {
      if (!filters.selectedProvince) {
        filters.setSelectedProvince(lockedProvince);
      }
      if (!cursorInUrl) {
        const currentSearch = urlParams.get("search") || "";
        const currentCity = urlParams.get("city") || undefined;
        filters.updateFiltersInUrl(currentSearch, lockedProvince, currentCity);
        membersData.setCurrentCursor(null);
        membersData.fetchMembers(
          "next",
          null,
          currentSearch || undefined,
          lockedProvince,
          currentCity,
          undefined,
          undefined,
          undefined,
          filters.tipeIar,
          filters.sortBy,
          filters.sortOrder
        );
      }
      return;
    }
    
    // If URL has a province but state doesn't, sync state from URL
    if (hasProvinceInUrl && urlProvince && urlProvince !== filters.selectedProvince) {
      filters.setSelectedProvince(urlProvince);
    }
    
    // Check if there are any URL params (user has manually set filters or pagination)
    const hasUrlParams =
      urlParams.has("province") ||
      urlParams.has("city") ||
      urlParams.has("search") ||
      urlParams.has("startDate") ||
      urlParams.has("endDate") ||
      urlParams.has("cursor");
    
    // Only apply default location scope if no URL params and location scope exists
    // Nasional (stage 3): full country — do not auto-narrow from province/city scope
    if (!hasUrlParams && locationScope && user && !isNasionalStage3) {
      // If province level, only set default if there's exactly one province (better UX)
      // If multiple provinces, let locationFilter restrict results but don't auto-select
      if (locationScope.level === "province" && locationScope.provinces.length === 1) {
        const defaultProvince = locationScope.provinces[0];
        filters.setSelectedProvince(defaultProvince);
        filters.updateFiltersInUrl("", defaultProvince, undefined);
        
        // Fetch members with default province filter
        membersData.setCurrentCursor(null);
        membersData.fetchMembers(
          "next",
          null,
          undefined,
          defaultProvince,
          undefined,
          undefined,
          undefined,
          undefined,
          filters.tipeIar,
          filters.sortBy,
          filters.sortOrder
        );
      }
      // If city level, set first city as default
      else if (locationScope.level === "city" && locationScope.cities.length > 0) {
        // For city level, we need to find which province the city belongs to
        // For now, we'll just filter by cities on the backend
        // The backend should handle city filtering
        const defaultCity = locationScope.cities[0];
        filters.setSelectedCity(defaultCity);
        filters.updateFiltersInUrl("", undefined, defaultCity);
        
        // Fetch members with default city filter
        membersData.setCurrentCursor(null);
        membersData.fetchMembers(
          "next",
          null,
          undefined,
          undefined,
          defaultCity,
          undefined,
          undefined,
          undefined,
          filters.tipeIar,
          filters.sortBy,
          filters.sortOrder
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedProvince, lockedCity, isCityAdminStage1, isNasionalStage3]); // Wilayah admin: sync URL when scope resolves

  // Reset password dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = React.useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = React.useState<string | null>(null);
  const [resetPasswordUserIar, setResetPasswordUserIar] = React.useState<string | undefined>(undefined);
  const [resetPasswordUserName, setResetPasswordUserName] = React.useState<string | undefined>(undefined);
  const [resetPasswordCallsign, setResetPasswordCallsign] = React.useState<string | undefined>(undefined);

  const [setEmailDialogOpen, setSetEmailDialogOpen] = React.useState(false);
  const [setEmailUserId, setSetEmailUserId] = React.useState<string | null>(null);
  const [setEmailUserName, setSetEmailUserName] = React.useState<string | undefined>(undefined);
  const [setEmailCallsign, setSetEmailCallsign] = React.useState<string | undefined>(undefined);
  const [setEmailCurrent, setSetEmailCurrent] = React.useState<string>("");
  
  // Sync state
  const [syncingUserId, setSyncingUserId] = React.useState<string | null>(null);
  
  // Export state
  const [exporting, setExporting] = React.useState(false);

  // Advanced search toggle
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // Handle search
  const handleSearch = () => {
    filters.updateFiltersInUrl(
      filters.searchQuery,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar
    );
    membersData.setCurrentCursor(null);
    membersData.fetchMembers(
      "next",
      null,
      filters.searchQuery || undefined,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      filters.sortBy,
      filters.sortOrder
    );
  };

  // Handle clear search
  const handleClearSearch = () => {
    filters.clearFilters();
    membersData.setCurrentCursor(null);
    membersData.fetchMembers("next", null);
  };

  // Handle location change
  const handleLocationChange = (province?: string, city?: string) => {
    membersData.setCurrentCursor(null);
    membersData.fetchMembers(
      "next",
      null,
      filters.searchQuery || undefined,
      province,
      city,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      filters.sortBy,
      filters.sortOrder
    );
  };
  
  // Handle sort change
  const handleSortChange = (field: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id", order: "asc" | "desc") => {
    filters.updateSort(field, order);
    membersData.setCurrentCursor(null);
    membersData.fetchMembers(
      "next",
      null,
      filters.searchQuery || undefined,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      field,
      order
    );
  };

  // Handle pagination
  const handleNextPage = () => {
    membersData.fetchMembers(
      "next",
      membersData.nextCursor,
      filters.searchQuery || undefined,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      filters.sortBy,
      filters.sortOrder
    );
  };

  const handlePreviousPage = () => {
    membersData.fetchMembers(
      "previous",
      membersData.previousCursor,
      filters.searchQuery || undefined,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      filters.sortBy,
      filters.sortOrder
    );
  };

  // Handle reset password
  const handleResetPassword = (
    userId: string,
    iar: string,
    name: string,
    callsign: string
  ) => {
    if (!hasAssignmentPrivilege(user, "manage_user_reset_password")) {
      toast.error("Anda tidak memiliki hak Reset password anggota (manage_user_reset_password).");
      return;
    }
    setResetPasswordUserId(userId);
    setResetPasswordUserIar(iar);
    setResetPasswordUserName(name);
    setResetPasswordCallsign(callsign);
    setResetPasswordDialogOpen(true);
  };

  const handleSetEmail = (
    userId: string,
    name: string,
    callsign: string,
    currentEmail: string
  ) => {
    if (!hasAssignmentPrivilege(user, "manage_user_set_email")) {
      toast.error("Anda tidak memiliki hak Set email anggota (manage_user_set_email).");
      return;
    }
    setSetEmailUserId(userId);
    setSetEmailUserName(name);
    setSetEmailCallsign(callsign);
    setSetEmailCurrent(currentEmail);
    setSetEmailDialogOpen(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    membersData.fetchMembers(
      membersData.currentDirection,
      membersData.currentCursor,
      filters.searchQuery || undefined,
      filters.selectedProvince,
      filters.selectedCity,
      filters.startDate,
      filters.endDate,
      filters.dateField,
      filters.tipeIar,
      filters.sortBy,
      filters.sortOrder
    );
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      
      // Build filters object from current filters
      const exportFilters: any = {};
      if (filters.searchQuery?.trim()) {
        exportFilters.search = filters.searchQuery.trim();
      }
      if (filters.selectedProvince) {
        exportFilters.province = filters.selectedProvince;
      }
      if (filters.selectedCity) {
        exportFilters.city = filters.selectedCity;
      }
      if (filters.startDate) {
        exportFilters.startDate = filters.startDate.toISOString().split("T")[0];
      }
      if (filters.endDate) {
        exportFilters.endDate = filters.endDate.toISOString().split("T")[0];
      }
      if (filters.dateField) {
        exportFilters.dateField = filters.dateField;
      }
      if (filters.tipeIar) {
        exportFilters.tipe_iar = filters.tipeIar;
      }
      if (filters.sortBy) {
        exportFilters.sortBy = filters.sortBy;
      }
      if (filters.sortOrder) {
        exportFilters.sortOrder = filters.sortOrder;
      }

      await userService.exportMembersToExcel(exportFilters);
      toast.success("Data berhasil diekspor ke Excel");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error(error instanceof Error ? error.message : "Gagal mengekspor data ke Excel");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6 overflow-x-hidden max-w-[1400px] mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Daftar Anggota</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Kelola data anggota ORARI</p>
        </div>
        <Button
          onClick={handleExportToExcel}
          disabled={exporting || membersData.loading}
          variant="outline"
          className="gap-2"
        >
          {exporting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Mengekspor...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Ekspor ke Excel
            </>
          )}
        </Button>
      </div>

      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari berdasarkan callsign atau nama..."
            value={filters.searchQuery}
            onChange={(e) => filters.setSearchQuery(e.target.value)}
            className="pl-9 h-9"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </div>
        <Button onClick={handleSearch} disabled={membersData.loading} size="sm" className="h-9 px-4">
          Cari
        </Button>
        <Button
          variant={showAdvanced ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="h-9 gap-1.5"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filter Lanjutan
        </Button>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <MemberFilters
        filters={filters}
        loading={membersData.loading}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        onLocationChange={handleLocationChange}
        allowedProvinces={allowedProvinces}
        provinceLocked={provinceFilterLocked}
        allowedCities={allowedCities}
        cityLocked={cityFilterLocked}
        onDateRangeChange={() => {
          membersData.setCurrentCursor(null);
          // Use refs to get latest values immediately without waiting for state update
          membersData.fetchMembers(
            "next",
            null,
            filters.searchQueryRef.current || undefined,
            filters.selectedProvinceRef.current,
            filters.selectedCityRef.current,
            filters.startDateRef.current,
            filters.endDateRef.current,
            filters.dateFieldRef.current,
            filters.tipeIarRef.current,
            filters.sortByRef.current,
            filters.sortOrderRef.current
          );
        }}
                  />
      )}

      {/* Members Table */}
      <MemberTable
        members={membersData.members}
        loading={membersData.loading}
        error={membersData.error}
        hasNext={membersData.hasNext}
        hasPrevious={membersData.hasPrevious}
        nextCursor={membersData.nextCursor}
        previousCursor={membersData.previousCursor}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        syncingUserId={syncingUserId}
        onSyncStart={setSyncingUserId}
        onSyncEnd={() => setSyncingUserId(null)}
        onResetPassword={handleResetPassword}
        onSetEmail={handleSetEmail}
        onRefresh={handleRefresh}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSortChange={handleSortChange}
      />

      {/* Reset Password Dialog */}
      {resetPasswordUserId && (
        <ResetPasswordDialog
          open={resetPasswordDialogOpen}
          onOpenChange={setResetPasswordDialogOpen}
          userId={resetPasswordUserId}
          userIar={resetPasswordUserIar}
          userName={resetPasswordUserName}
          callsign={resetPasswordCallsign}
        />
      )}

      {setEmailUserId && (
        <SetEmailDialog
          open={setEmailDialogOpen}
          onOpenChange={setSetEmailDialogOpen}
          userId={setEmailUserId}
          userName={setEmailUserName}
          callsign={setEmailCallsign}
          currentEmail={setEmailCurrent}
          onSuccess={handleRefresh}
        />
      )}
    </div>
  );
}
